/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useMemo } from "react";
import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";
import { detectToolsOrigin } from "../../../utils/helper.js";
import axios from "axios";

// ---------- Helpers ----------
const parseNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clampNonNegative = (n) => Math.max(0, n || 0);

const formatCurrency = (value) =>
  Math.round(Number(value) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const is65OrOlderOn = (dobStr, taxYear) => {
  if (!dobStr) return false;
  const cutoff = new Date(`${taxYear}-12-31`);
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return false;

  let age = cutoff.getFullYear() - dob.getFullYear();
  const m = cutoff.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && cutoff.getDate() < dob.getDate())) age--;

  return age >= 65;
};

const steps = ["Basic Info", "Income", "Deductions", "Credits"];

// ✅ Only keep what UI displays (results + inputs)
const buildUiPayload = ({ formData, calculated, taxYear }) => {
  // ✅ FIXED: filingStatus strings are now human-readable everywhere
  const fsKeyMap = {
    "Single": "single",
    "Married Filing Jointly": "marriedJointly",
    "Married Filing Separately": "marriedSeparately",
    "Head of Household": "headOfHousehold",
  };

  const filingStatusKey = fsKeyMap[formData.filingStatus] || "";

  // -------- Inputs shown / relevant --------
  const inputs_json = {
    taxYear: String(formData.taxYear || taxYear),
    filingStatus: formData.filingStatus || "",
    dateOfBirth: formData.dateOfBirth || "",
    numberOfDependents: clampNonNegative(parseNum(formData.numberOfDependents)),
    totalIncome: clampNonNegative(parseNum(formData.totalIncome)),
    adjustmentsToIncome: clampNonNegative(parseNum(formData.adjustmentsToIncome)),
    deductionType: formData.deductionType || "standard",
    taxCredits: clampNonNegative(parseNum(formData.taxCredits)),
    federalWithholding: clampNonNegative(parseNum(formData.federalWithholding)),
  };

  // Only include itemized fields if user selected itemized
  if (formData.deductionType === "itemized") {
    inputs_json.itemizedDeductions = {
      mortgageInterest: clampNonNegative(
        parseNum(formData.itemizedDeductions?.mortgageInterest)
      ),
      stateLocalTaxes: clampNonNegative(
        parseNum(formData.itemizedDeductions?.stateLocalTaxes)
      ),
      charitableContributions: clampNonNegative(
        parseNum(formData.itemizedDeductions?.charitableContributions)
      ),
      medicalExpenses: clampNonNegative(
        parseNum(formData.itemizedDeductions?.medicalExpenses)
      ),
    };
  }

  // (Optional) include computed standard deduction displayed in the alert
  if (formData.deductionType === "standard") {
    inputs_json.standardDeductionShown = calculated?.deduction ?? 0;
  }

  // -------- Results: ONLY what's visible in Results card --------
  const results_json = {
    totalIncome: calculated?.totalIncome ?? 0,
    AGI: calculated?.AGI ?? 0,
    deduction: calculated?.deduction ?? 0,
    taxableIncome: calculated?.taxableIncome ?? 0,
    tax: calculated?.tax ?? 0,
    credits: calculated?.credits ?? 0,
    totalTax: calculated?.totalTax ?? 0,
    refundOrOwed: calculated?.refundOrOwed ?? 0,
    effectiveRate: calculated?.effectiveRate ?? 0, // percent already (0-100)
    marginalRate: calculated?.marginalRate ?? 0, // percent already (0-100)
  };

  return { inputs_json, results_json, filingStatusKey };
};

// ---------- Year-specific TAX CONFIG ----------
const TAX_CONFIG = {
  2025: {
    SENIOR_ADD_ON: {
      single: 2000,
      headOfHousehold: 2000,
      marriedJointly: 1600,
      marriedSeparately: 1600,
    },
    STANDARD_DEDUCTIONS: {
      single: 15000,
      marriedJointly: 30000,
      marriedSeparately: 15000,
      headOfHousehold: 22500,
    },
    TAX_BRACKETS: {
      single: [
        { min: 0, max: 11925, rate: 0.1 },
        { min: 11926, max: 48475, rate: 0.12 },
        { min: 48476, max: 103350, rate: 0.22 },
        { min: 103351, max: 197300, rate: 0.24 },
        { min: 197301, max: 250525, rate: 0.32 },
        { min: 250526, max: 626350, rate: 0.35 },
        { min: 626351, max: Infinity, rate: 0.37 },
      ],
      marriedJointly: [
        { min: 0, max: 23850, rate: 0.1 },
        { min: 23851, max: 96950, rate: 0.12 },
        { min: 96951, max: 206700, rate: 0.22 },
        { min: 206701, max: 394600, rate: 0.24 },
        { min: 394601, max: 501050, rate: 0.32 },
        { min: 501051, max: 751600, rate: 0.35 },
        { min: 751601, max: Infinity, rate: 0.37 },
      ],
      marriedSeparately: [
        { min: 0, max: 11925, rate: 0.1 },
        { min: 11926, max: 48475, rate: 0.12 },
        { min: 48476, max: 103350, rate: 0.22 },
        { min: 103351, max: 197300, rate: 0.24 },
        { min: 197301, max: 250525, rate: 0.32 },
        { min: 250526, max: 375800, rate: 0.35 },
        { min: 375801, max: Infinity, rate: 0.37 },
      ],
      headOfHousehold: [
        { min: 0, max: 17000, rate: 0.1 },
        { min: 17001, max: 64850, rate: 0.12 },
        { min: 64851, max: 103350, rate: 0.22 },
        { min: 103351, max: 197300, rate: 0.24 },
        { min: 197301, max: 250525, rate: 0.32 },
        { min: 250526, max: 626350, rate: 0.35 },
        { min: 626351, max: Infinity, rate: 0.37 },
      ],
    },
  },

  2026: {
    SENIOR_ADD_ON: {
      single: 2050,
      headOfHousehold: 2050,
      marriedJointly: 1650,
      marriedSeparately: 1650,
    },
    STANDARD_DEDUCTIONS: {
      single: 16100,
      marriedJointly: 32200,
      marriedSeparately: 16100,
      headOfHousehold: 24150,
    },
    TAX_BRACKETS: {
      single: [
        { min: 0, max: 12_400, rate: 0.1 },
        { min: 12_401, max: 50_400, rate: 0.12 },
        { min: 50_401, max: 105_700, rate: 0.22 },
        { min: 105_701, max: 201_775, rate: 0.24 },
        { min: 201_776, max: 256_225, rate: 0.32 },
        { min: 256_226, max: 640_600, rate: 0.35 },
        { min: 640_601, max: Infinity, rate: 0.37 },
      ],

      marriedJointly: [
        { min: 0, max: 24_800, rate: 0.1 },
        { min: 24_801, max: 100_800, rate: 0.12 },
        { min: 100_801, max: 211_400, rate: 0.22 },
        { min: 211_401, max: 403_550, rate: 0.24 },
        { min: 403_551, max: 512_450, rate: 0.32 },
        { min: 512_451, max: 768_700, rate: 0.35 },
        { min: 768_701, max: Infinity, rate: 0.37 },
      ],

      marriedSeparately: [
        { min: 0, max: 12_400, rate: 0.1 },
        { min: 12_401, max: 50_400, rate: 0.12 },
        { min: 50_401, max: 105_700, rate: 0.22 },
        { min: 105_701, max: 201_775, rate: 0.24 },
        { min: 201_776, max: 256_225, rate: 0.32 },
        { min: 256_226, max: 384_350, rate: 0.35 },
        { min: 384_351, max: Infinity, rate: 0.37 },
      ],

      headOfHousehold: [
        { min: 0, max: 17_700, rate: 0.1 },
        { min: 17_701, max: 67_450, rate: 0.12 },
        { min: 67_451, max: 105_700, rate: 0.22 },
        { min: 105_701, max: 201_750, rate: 0.24 },
        { min: 201_751, max: 256_200, rate: 0.32 },
        { min: 256_201, max: 640_600, rate: 0.35 },
        { min: 640_601, max: Infinity, rate: 0.37 },
      ],
    },
  },
};

// ---------- Gating storage helpers (24h, shared across calculators) ----------
const UNK_GATE_KEY = "unk_calc_unlock_v1";
const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

const readUnlock = () => {
  try {
    const raw = localStorage.getItem(UNK_GATE_KEY);
    if (!raw) return { unlocked: false, email: "" };
    const parsed = JSON.parse(raw);
    const expiresAt = Number(parsed?.expiresAt || 0);
    const email = String(parsed?.email || "");
    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(UNK_GATE_KEY);
      return { unlocked: false, email: "" };
    }
    return { unlocked: true, email };
  } catch {
    return { unlocked: false, email: "" };
  }
};

const writeUnlock = ({ email }) => {
  const payload = {
    expiresAt: Date.now() + UNK_GATE_TTL_MS,
    email: String(email || ""),
  };
  localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
};

// ---------- PROXY ----------
const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

// ---------- Component ----------
export default function FederalTaxCalculator({
  year = 2025,
  calculatorName = "Federal Tax Calculator",
  endpoint = PROXY_URL,
}) {
  const taxYear = String(year);
  const { SENIOR_ADD_ON, STANDARD_DEDUCTIONS, TAX_BRACKETS } =
    TAX_CONFIG[year] || TAX_CONFIG[2025];

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const resultsRef = useRef(null);

  const [formData, setFormData] = useState({
    taxYear,
    filingStatus: "",
    dateOfBirth: "",
    numberOfDependents: "",
    totalIncome: "",
    adjustmentsToIncome: "",
    deductionType: "standard",
    itemizedDeductions: {
      mortgageInterest: "",
      stateLocalTaxes: "",
      charitableContributions: "",
      medicalExpenses: "",
    },
    taxCredits: "",
    federalWithholding: "",
  });

  const [calculated, setCalculated] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  // ✅ KEEP LATEST payload, like Crypto
  const latestPayloadRef = useRef({
    inputs_json: null,
    results_json: null,
  });

  // --- gating state ---
  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  // modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  const goNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep((prev) => prev + 1);
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  // ✅ FIXED: filingStatus is now human-readable everywhere
  const getFilingStatusKey = () => {
    switch (formData.filingStatus) {
      case "Single":
        return "single";
      case "Married Filing Jointly":
        return "marriedJointly";
      case "Married Filing Separately":
        return "marriedSeparately";
      case "Head of Household":
        return "headOfHousehold";
      default:
        return null;
    }
  };

  const fsKey = getFilingStatusKey();

  // ✅ FIXED: HOH validation uses the same string as select value
  const isHOHInvalid =
    formData.filingStatus === "Head of Household" &&
    parseNum(formData.numberOfDependents) < 1;

  const isLastStep = currentStep === steps.length - 1;

  // ✅ calculation logic unchanged — but now we also update latestPayloadRef
  useEffect(() => {
    const income = clampNonNegative(parseNum(formData.totalIncome));
    const adjustments = clampNonNegative(parseNum(formData.adjustmentsToIncome));
    const AGI = clampNonNegative(income - adjustments);

    let deduction = 0;

    if (formData.deductionType === "standard") {
      if (fsKey) {
        deduction = STANDARD_DEDUCTIONS[fsKey] || 0;

        const addOn = SENIOR_ADD_ON[fsKey] || 0;
        const yearNum = Number(formData.taxYear || taxYear);
        const count = is65OrOlderOn(formData.dateOfBirth, yearNum) ? 1 : 0;
        deduction += count * addOn;
      }
    } else {
      deduction = Object.values(formData.itemizedDeductions).reduce(
        (sum, val) => sum + clampNonNegative(parseNum(val)),
        0
      );
    }

    const taxableIncome = clampNonNegative(AGI - deduction);

    let tax = 0;
    let marginalRate = 0;

    if (fsKey && taxableIncome > 0) {
      const brackets = TAX_BRACKETS[fsKey] || [];
      let lower = 0;

      for (const b of brackets) {
        const cap = b.max;
        const upper = Math.min(taxableIncome, cap);
        if (upper > lower) {
          tax += (upper - lower) * b.rate;
          marginalRate = b.rate;
          lower = upper;
        }
        if (taxableIncome <= cap) break;
      }
    }

    const credits = clampNonNegative(parseNum(formData.taxCredits));
    const withholding = clampNonNegative(parseNum(formData.federalWithholding));
    const totalTax = clampNonNegative(tax - credits);
    const refundOrOwed = withholding - totalTax;

    const effectiveRate =
      taxableIncome > 0 ? (totalTax / taxableIncome) * 100 : 0;

    const computed = {
      totalIncome: income,
      AGI,
      deduction,
      taxableIncome,
      tax,
      credits,
      totalTax,
      refundOrOwed,
      effectiveRate,
      marginalRate: marginalRate * 100,
    };

    setCalculated(computed);

    // ✅ keep latest payload for emailing
    const uiPayload = buildUiPayload({
      formData,
      calculated: computed,
      taxYear,
      calculatorName,
    });

    latestPayloadRef.current = {
      inputs_json: uiPayload.inputs_json,
      results_json: uiPayload.results_json,
    };
  }, [
    formData.filingStatus,
    formData.dateOfBirth,
    formData.numberOfDependents,
    formData.totalIncome,
    formData.adjustmentsToIncome,
    formData.deductionType,
    formData.itemizedDeductions?.mortgageInterest,
    formData.itemizedDeductions?.stateLocalTaxes,
    formData.itemizedDeductions?.charitableContributions,
    formData.itemizedDeductions?.medicalExpenses,
    formData.taxCredits,
    formData.federalWithholding,
  ]);

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ Send results (uses latestPayloadRef like Crypto)
  const sendResultsToEmail = async (emailToSend) => {
    setSendingResults(true);

    try {
      const payload = latestPayloadRef.current || {};
      const safeInputs = payload.inputs_json || {};
      const safeResults = payload.results_json || {};

      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: taxYear,
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: safeInputs,
            pdf_notes: {
              disclaimer:
                "This calculator provides estimates only. Consult a tax professional for personalized advice.",
            },
            results_json: safeResults,
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();

      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  // ✅ Update GHL contact email (old -> new)
  const updateGhlContactEmail = async ({ oldEmail, newEmail }) => {
    const resp = await axios.post(
      PROXY_URL,
      {
        path: "/public/update-contact-email-ghl",
        method: "POST",
        raw: true,
        data: {
          oldEmail,
          newEmail,
        },
      },
      {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      }
    );

    if (resp?.data?.success === false) {
      throw new Error(
        resp?.data?.message ||
          resp?.data?.error ||
          "Failed to update GHL contact email"
      );
    }

    return resp?.data;
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    const newE = String(newEmail || "").trim().toLowerCase();
    const oldE = String(storedEmail || "").trim().toLowerCase();

    setSendingResults(true);
    try {
      if (oldE && newE && oldE !== newE) {
        await updateGhlContactEmail({ oldEmail: oldE, newEmail: newE });
      }

      writeUnlock({ email: newE });
      setStoredEmail(newE);
      setUnlocked(true);

      await sendResultsToEmail(newE);
    } catch (e) {
      console.error("handleUpdateEmailAndSend error:", e);
      throw e;
    } finally {
      setSendingResults(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Tax Year</label>
              <select className="form-select" value={taxYear} disabled>
                <option value={taxYear}>{taxYear}</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Filing Status</label>

              {/* ✅ FIXED: option values are now the full words */}
              <select
                className="form-select"
                value={formData.filingStatus}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    filingStatus: e.target.value,
                  }))
                }
              >
                <option value="">Select status</option>
                <option value="Single">Single</option>
                <option value="Married Filing Jointly">
                  Married Filing Jointly
                </option>
                <option value="Married Filing Separately">
                  Married Filing Separately
                </option>
                <option value="Head of Household">Head of Household</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Date of Birth</label>
              <input
                type="date"
                className="form-control"
                value={formData.dateOfBirth}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dateOfBirth: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted">
                  Used to determine age for certain tax benefits.
                </small>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Number of Dependents
              </label>
              {isHOHInvalid && (
                <div className="mt-2">
                  <small className="text-danger">
                    Head of Household requires at least one qualifying
                    dependent.
                  </small>
                </div>
              )}
              <input
                type="number"
                className="form-control"
                value={formData.numberOfDependents}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    numberOfDependents: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted">
                  Includes qualifying children and relatives.
                </small>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Total Income</label>
              <input
                type="number"
                className="form-control"
                value={formData.totalIncome}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    totalIncome: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted mt-2">
                  Include wages, self-employment income, interest, dividends,
                  etc.
                </small>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                Adjustments to Income
              </label>
              <input
                type="number"
                className="form-control"
                value={formData.adjustmentsToIncome}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    adjustmentsToIncome: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted mt-2">
                  IRA contributions, student loan interest, educator expenses,
                  etc.
                </small>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Deduction Type</label>
              <select
          className="form-select"
          value={formData.deductionType}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              deductionType: e.target.value,
            }))
          }
              >
          <option value="">Select deduction</option>
          <option value="standard">Standard Deduction</option>
          <option value="itemized">Itemized Deductions</option>
              </select>
            </div>

            {formData.deductionType === "standard" && (
              <div className="col-12">
          <div className="alert alert-primary">
            <strong>{taxYear} Standard Deduction:</strong>{" "}
            {fsKey
              ? formatCurrency(STANDARD_DEDUCTIONS[fsKey])
              : formatCurrency(0)}
            {fsKey &&
              is65OrOlderOn(
                formData.dateOfBirth,
                Number(formData.taxYear || taxYear)
              ) && (
                <>
            {" "}
            <strong>+ Senior Add-On:</strong>{" "}
            {formatCurrency(SENIOR_ADD_ON[fsKey] || 0)}
                </>
              )}
          </div>
              </div>
            )}

            {formData.deductionType === "itemized" && (
              <div className="row g-3 mt-2">
          {Object.entries(formData.itemizedDeductions).map(([key, val]) => {
            // Convert key to label: Mortgage, State Local Taxes, Charitable Contributions, Medical Expenses
            const label =
              key === "mortgageInterest"
                ? "Mortgage"
                : key === "stateLocalTaxes"
                ? "State Local Taxes"
                : key === "charitableContributions"
                ? "Charitable Contributions"
                : key === "medicalExpenses"
                ? "Medical Expenses"
                : key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <div key={key} className="col-md-6">
                <label className="form-label fw-semibold">
            {label}
                </label>
                <input
            type="number"
            className="form-control"
            value={val}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                itemizedDeductions: {
                  ...prev.itemizedDeductions,
                  [key]: e.target.value,
                },
              }))
            }
                />
              </div>
            );
          })}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Tax Credits</label>
              <input
                type="number"
                className="form-control"
                value={formData.taxCredits}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    taxCredits: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted mt-2">
                  Child tax credit, education credits, earned income credit,
                  etc.
                </small>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                Federal Tax Withholding
              </label>
              <input
                type="number"
                className="form-control"
                value={formData.federalWithholding}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    federalWithholding: e.target.value,
                  }))
                }
              />
              <div className="mt-2">
                <small className="text-muted mt-2">
                  Total federal income tax withheld from paychecks or estimated
                  payments.{" "}
                  <a
                    href="https://www.irs.gov/individuals/tax-withholding-estimator"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Use the IRS Tax Withholding Estimator
                  </a>{" "}
                  if you are unsure.
                </small>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        .unk-results-wrap { position: relative; }
        .unk-results-blur { filter: blur(10px); user-select: none; pointer-events: none; }
        .unk-results-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          text-align: center;
          background: rgba(255,255,255,0.82);
          backdrop-filter: blur(2px);
          border-radius: 0.375rem;
          cursor: pointer;
        }
        .unk-overlay-card {
          max-width: 320px;
          width: 100%;
          border-radius: 14px;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          border: 1px solid rgba(0,0,0,0.06);
          padding: 18px 16px;
        }
        .unk-overlay-title { font-weight: 800; font-size: 18px; margin-bottom: 6px; }
        .unk-overlay-body { color: #6c757d; font-size: 14px; margin-bottom: 12px; }
        .unk-overlay-btn {
          background: #0f8a4c;
          border: 1px solid #0f8a4c;
          color: white;
          width: 100%;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 700;
        }
      `}</style>

      <div className="row justify-content-center tax-calculator-wrapper w-100">
        <div className="col-md-8">
          <div className="card shadow tax-form-card p-4">
            <div className="bg-primary text-white p-2 rounded d-flex justify-content-between align-items-center">
              <span>Calculate Your Federal Income Tax</span>
              <span className="badge bg-light text-primary">
                Tax Year {taxYear}
              </span>
            </div>

            <div className="row justify-content-center bg-primary-subtle rounded-pill mb-4 px-2 py-2 mt-4 mx-0 gx-2">
              {steps.map((label, index) => (
                <div
                  className="col-6 col-sm-4 col-md-3 col-lg-auto d-flex justify-content-center"
                  key={label}
                >
                  <button
                    className={`btn rounded-pill fw-semibold px-4 py-2 w-100 ${
                      currentStep === index
                        ? "bg-primary text-white"
                        : "bg-white text-dark"
                    }`}
                    onClick={() => setCurrentStep(index)}
                    style={{
                      minWidth: "130px",
                      whiteSpace: "nowrap",
                      border: "none",
                      boxShadow: "inset 0 0 0 1px #dee2e6",
                    }}
                  >
                    {label}
                  </button>
                </div>
              ))}
            </div>

            {renderStep()}

            <div className="d-flex justify-content-between mt-4">
              <button
                className="btn btn-light border"
                onClick={goPrev}
                disabled={currentStep === 0}
              >
                Previous
              </button>

              <button
                className={`btn ${isLastStep ? "btn-success" : "btn-dark"}`}
                onClick={() => {
                  if (!isLastStep) {
                    goNext();
                    return;
                  }
                  openGateFlow();
                }}
                style={
                  isLastStep
                    ? { background: "#0f8a4c", borderColor: "#0f8a4c" }
                    : undefined
                }
              >
                {!isLastStep ? "Next" : "View Results"}
              </button>
            </div>

            {isLastStep && (
              <div className="mt-3">
                <small className="text-muted">
                  Click <strong>View Results</strong> to unlock your
                  calculation.
                </small>
              </div>
            )}
          </div>
        </div>

        {calculated && (
          <div className="col-md-4">
            <div className="card shadow tax-results-card p-4" ref={resultsRef}>
              <div
                className="bg-success text-white p-2 rounded"
                style={{ background: "#0f8a4c" }}
              >
                Tax Calculation Preview
              </div>

              <div
                className="unk-results-wrap mt-3"
                style={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "0.375rem",
                  background: !unlocked
                    ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.92))"
                    : "transparent",
                }}
              >
                {!unlocked && (
                  <div
                    style={{
                      position: "absolute",
                      inset: "-30px",
                      background:
                        "radial-gradient(circle at 20% 20%, rgba(15,138,76,0.18), transparent 55%)," +
                        "radial-gradient(circle at 80% 70%, rgba(13,110,253,0.14), transparent 55%)," +
                        "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9))",
                      filter: "blur(18px)",
                      transform: "scale(1.05)",
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                )}

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div className={!unlocked ? "unk-results-blur" : ""}>
                    <ul className="list-group list-group-flush rounded border">
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Total Income:</span>
                        <span>{formatCurrency(calculated.totalIncome)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Adjusted Gross Income:</span>
                        <span>{formatCurrency(calculated.AGI)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Deductions:</span>
                        <span>{formatCurrency(calculated.deduction)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Taxable Income:</span>
                        <span>{formatCurrency(calculated.taxableIncome)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Federal Tax:</span>
                        <span>{formatCurrency(calculated.tax)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Total Credits:</span>
                        <span>{formatCurrency(calculated.credits)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span className="fw-bold">
                          Final Tax (after credits):
                        </span>
                        <span>{formatCurrency(calculated.totalTax)}</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span className="fw-bold">
                          {calculated.refundOrOwed >= 0
                            ? "Refund:"
                            : "Amount Owed:"}
                        </span>
                        <span>
                          {formatCurrency(Math.abs(calculated.refundOrOwed))}
                        </span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Effective Rate (Final Tax ÷ Taxable Income):</span>
                        <span>{calculated.effectiveRate.toFixed(2)}%</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Marginal Rate:</span>
                        <span>{calculated.marginalRate.toFixed(2)}%</span>
                      </li>
                    </ul>
                  </div>

                  {!unlocked && (
                    <div
                      onClick={openGateFlow}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openGateFlow();
                      }}
                      aria-label="Results locked. Open unlock modal."
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.25)",
                        backdropFilter: "blur(16px) saturate(140%)",
                        WebkitBackdropFilter: "blur(16px) saturate(140%)",
                        borderRadius: "0.375rem",
                        cursor: "pointer",
                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: 320,
                          width: "100%",
                          borderRadius: 14,
                          background: "#fff",
                          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          padding: "18px 16px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 18,
                            marginBottom: 6,
                          }}
                        >
                          Results Locked
                        </div>
                        <div
                          style={{
                            color: "#6c757d",
                            fontSize: 14,
                            marginBottom: 12,
                          }}
                        >
                          Enter your name and email to view your results
                          instantly.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => {
          handleLeadSuccess(payload);
        }}
        calculatorName={calculatorName}
        calculatorYear={taxYear}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || {}}
        inputsJson={latestPayloadRef.current?.inputs_json || {}}
        pdf_notes={{
          disclaimer:
            "This calculator provides estimates only. Consult a tax professional for personalized advice.",
        }}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || {}}
        inputsJson={latestPayloadRef.current?.inputs_json || {}}
        calculatorName={calculatorName}
        calculatorYear={taxYear}
        pageUrl={pageUrl}
        pdf_notes={{
          disclaimer:
            "This calculator provides estimates only. Consult a tax professional for personalized advice.",
        }}
      />
    </>
  );
}
